import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NoticeIconList, NoticeIconModule, NoticeItem } from '@delon/abc/notice-icon';
import { ALAIN_I18N_TOKEN } from '@delon/theme';
import { formatDistanceToNow } from 'date-fns';
import { CommonApiService } from 'projects/bo/src/app/core/services/api/common/common-api.service';
import { GetNotificationRequest, NotificationDto } from 'projects/bo/src/app/core/services/api/common/common.model';
import { FarmState } from 'projects/bo/src/app/core/services/states/farm.service';
import { EMPTY, interval, merge, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, startWith, switchMap } from 'rxjs/operators';
import { I18NService } from '../../../core/i18n/i18n.service';

@Component({
  selector: 'header-notify',
  template: `
    <notice-icon
      [data]="data"
      [count]="count"
      [loading]="loading"
      btnClass="alain-default__nav-item"
      btnIconClass="alain-default__nav-item-icon"
      (popoverVisibleChange)="onPopoverVisibleChange($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NoticeIconModule]
})
export class HeaderNotifyComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly commonApi = inject(CommonApiService);
  private readonly farmState = inject(FarmState);

  data: NoticeItem[] = [
    {
      title: this.i18n.instant('common.notification.unReadTitle'),
      list: [],
      emptyText: this.i18n.instant('common.notification.unReadEmpty'),
      emptyImage: 'https://gw.alipayobjects.com/zos/rmsportal/wAhyIChODzsoKIOBHcBk.svg'
    },
    {
      title: this.i18n.instant('common.notification.readTitle'),
      list: [],
      emptyText: this.i18n.instant('common.notification.readEmpty'),
      emptyImage: 'https://gw.alipayobjects.com/zos/rmsportal/sAuJeJzSKbUmHfBQRzmZ.svg'
    }
  ];

  count = 0;
  loading = false;
  private isPopoverOpen = false;
  private unreadNotificationIds: number[] = [];

  // 10 minutes interval for background polling
  private readonly POLLING_INTERVAL = 15 * 60 * 1000; // 15 minutes

  ngOnInit(): void {
    // Initial/Farm Change Trigger
    const farmChange$ = this.farmState.get$().pipe(distinctUntilChanged((prev: any, curr: any) => prev?.farmId === curr?.farmId));

    // Polling Trigger
    const polling$ = interval(this.POLLING_INTERVAL).pipe(filter(() => !this.isPopoverOpen));

    merge(farmChange$, polling$)
      .pipe(
        switchMap(() => this.getNotificationReadCount$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(unreadCount => {
        this.count = Math.min(unreadCount, 99);
        this.cdr.detectChanges();
      });
  }

  private getNotificationReadCount$(): Observable<number> {
    const farmId = this.farmState.get()?.farmId;

    if (!farmId) {
      return of(0);
    }

    return this.commonApi.GetNotificationRead({ farmId }).pipe(
      switchMap((response: any) => [response.data.unreadCount]),
      catchError(error => {
        console.error('Fetch failed:', error);
        return of(0);
      })
    );
  }

  private startBackgroundPolling(): void {
    interval(this.POLLING_INTERVAL)
      .pipe(
        startWith(0),
        filter(() => !this.isPopoverOpen), // Only poll when popover is closed
        switchMap(() => {
          const farmId = this.farmState.get()?.farmId;
          if (!farmId) return EMPTY;

          return this.commonApi.GetNotificationRead({
            farmId
          });
        }),
        catchError(error => {
          console.error('Background notification polling failed:', error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(response => {
        // Update badge count, max 99
        this.count = Math.min(response.data.unreadCount, 99);
        this.cdr.detectChanges();
      });
  }

  onPopoverVisibleChange(visible: boolean): void {
    this.isPopoverOpen = visible;
    if (visible) {
      this.loadNotifications();
    }
  }

  private loadNotifications(): void {
    if (this.loading) return;

    const farmId = this.farmState.get()?.farmId;
    if (!farmId) {
      console.warn('No farmId available for loading notifications');
      return;
    }

    this.loading = true;

    const request: GetNotificationRequest = {
      farmId
    };

    this.commonApi
      .GetNotification(request)
      .pipe(
        catchError(error => {
          console.error('Failed to load notifications:', error);
          this.loading = false;
          this.cdr.detectChanges();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => {
        if (res.data.list) {
          this.updateNotificationData(res.data.list);

          // Store unread notification IDs for later marking as read
          this.unreadNotificationIds = res.data.list.filter(n => !n.isRead).map(n => n.notificationId);

          // Mark all unread notifications as read when popover opens
          if (this.unreadNotificationIds.length > 0) {
            this.markNotificationsAsRead();
          }
        }

        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  private updateNotificationData(notifications: NotificationDto[]): void {
    const unreadNotifications: NoticeIconList[] = [];
    const readNotifications: NoticeIconList[] = [];

    notifications.forEach(notification => {
      const noticeItem: NoticeIconList = {
        id: notification.notificationId.toString(),
        title: notification.message,
        datetime: formatDistanceToNow(new Date(notification.createDate)),
        read: false // due to the unread item color
      };

      if (notification.isRead) {
        readNotifications.push(noticeItem);
      } else {
        unreadNotifications.push(noticeItem);
      }
    });

    // Update data arrays
    this.data[0].list = unreadNotifications; // unread
    this.data[1].list = readNotifications; // read
  }

  private markNotificationsAsRead(): void {
    const farmId = this.farmState.get()?.farmId;
    if (!farmId || this.unreadNotificationIds.length === 0) return;

    this.commonApi
      .ReadNotification({
        farmId,
        notificationIds: this.unreadNotificationIds
      })
      .pipe(
        catchError(error => {
          console.error('Failed to mark notifications as read:', error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(response => {
        if (response) {
          // Update count and clear unread IDs
          this.count = 0;
          this.unreadNotificationIds = [];
          this.cdr.detectChanges();
        }
      });
  }
}
